import React, { useState, useEffect } from 'react';
import './ApprovalsDisclosure.scss';
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageBanner from "../../components/PageBanner";
import { FaChevronRight, FaFilePdf } from 'react-icons/fa';

import { getApprovals } from "../../services/data.service";
import { mediaUrl } from "../../services/assetUrl";

const ArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pdf-arrow">
    <path d="M9 6l6 6l-6 6"></path>
  </svg>
);

const PdfCard = ({ title, pdf, index = 0 }) => (
  <a href={pdf} target="_blank" rel="noreferrer" className={`approvals-card animate-fade-in delay-${index}`}>
    <div className="approvals-card__icon-wrap">
      <FaFilePdf className="pdf-icon-svg" />
    </div>
    <div className="approvals-card__content">
      <span className="approvals-card__title">{title}</span>
    </div>
    <div className="approvals-card__footer">
      <span className="approvals-card__action-text">View Document</span>
      <div className="approvals-card__arrow-wrap">
        <ArrowIcon />
      </div>
    </div>
  </a>
);

const DocumentRow = ({ title, pdf, index = 0 }) => (
  <a href={pdf} target="_blank" rel="noreferrer" className={`document-row animate-fade-in delay-${index}`}>
    <div className="document-row__left">
      <div className="document-row__icon">
        <FaFilePdf />
      </div>
      <span className="document-row__title">{title}</span>
    </div>
    <div className="document-row__btn">
      View <FaChevronRight size="12px" />
    </div>
  </a>
);

const TimelineRow = ({ title, pdf, index = 0 }) => (
  <div className={`timeline-item animate-fade-in delay-${index}`}>
    <div className="timeline-item__dot"></div>
    <a href={pdf} target="_blank" rel="noreferrer" className="timeline-item__card">
      <span className="timeline-item__badge">ACCREDITATION</span>
      <span className="timeline-item__title">{title}</span>
      <div className="timeline-item__action">
        View Document <FaChevronRight size="10px" />
      </div>
    </a>
  </div>
);

const ApprovalsDisclosure = () => {
  const [activeCategory, setActiveCategory] = useState("institutional");
  const [activeTab, setActiveTab] = useState("(2026-2030)");
  const [showAllInstitutional, setShowAllInstitutional] = useState(false);
  const [showAllAicte, setShowAllAicte] = useState(false);

  const [institutionalDisclosures, setInstitutionalDisclosures] = useState([]);
  const [vtuAffiliations, setVtuAffiliations] = useState([]);
  const [nbaAccreditations, setNbaAccreditations] = useState([]);
  const [aicteArchive, setAicteArchive] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getApprovals();
        if (res && res.success) {
          const data = res.data;
          // Resolve stored PDF paths to the S3 asset bucket (mediaUrl rewrites
          // legacy ncet.co.in hosts and /view /images keys onto REACT_APP_ASSET_BASE_URL).
          setInstitutionalDisclosures(data.filter(i => i.category === 'institutional').map(i => ({title: i.title, pdf: mediaUrl(i.pdfUrl)})));
          setVtuAffiliations(data.filter(i => i.category === 'vtu').map(i => ({title: i.title, pdf: mediaUrl(i.pdfUrl)})));
          setNbaAccreditations(data.filter(i => i.category === 'nba').map(i => ({title: i.title, pdf: mediaUrl(i.pdfUrl)})));
          
          const aicteData = data.filter(i => i.category === 'aicte');
          let archiveMap = {};
          aicteData.forEach(item => {
            if(!archiveMap[item.yearRange]) {
               archiveMap[item.yearRange] = [];
            }
            archiveMap[item.yearRange].push({title: item.title, pdf: mediaUrl(item.pdfUrl)});
          });
          setAicteArchive(archiveMap);
          
          const keys = Object.keys(archiveMap).sort().reverse();
          if (keys.length > 0 && (!activeTab || !keys.includes(activeTab))) {
            setActiveTab(keys[0]);
          }
        }
      } catch (err) {
        console.error("Failed to load approvals", err);
      }
    };
    fetchData();
  }, []);

  const categories = [
    { id: "institutional", label: "Institutional Disclosures" },
    { id: "vtu", label: "VTU Affiliations" },
    { id: "nba", label: "NBA Accreditations" },
    { id: "aicte", label: "AICTE Approvals (Archive)" }
  ];

  const renderContent = () => {
    switch (activeCategory) {
      case "institutional":
        const displayedInstitutional = showAllInstitutional ? institutionalDisclosures : institutionalDisclosures.slice(0, 3);
        return (
          <div className="animate-fade-up">
            <div className="content-header">
              <h2 className="content-header__title">Institutional Disclosures</h2>
              <p className="content-header__desc">Core institutional certificates and mandatory disclosures.</p>
            </div>
            <div className="approvals-grid-3">
              {displayedInstitutional.map((item, index) => (
                <PdfCard key={index} title={item.title} pdf={item.pdf} index={index} />
              ))}
            </div>
            {institutionalDisclosures.length > 3 && (
              <div className="load-more-container">
                <button 
                  className="load-more-btn" 
                  onClick={() => setShowAllInstitutional(!showAllInstitutional)}
                >
                  {showAllInstitutional ? "Show Less" : "Load More"}
                </button>
              </div>
            )}
          </div>
        );
      case "vtu":
        return (
          <div className="animate-fade-up">
            <div className="content-header">
              <h2 className="content-header__title">VTU Affiliations</h2>
              <p className="content-header__desc">Documents related to Visvesvaraya Technological University (VTU) affiliations and autonomy grants.</p>
            </div>
            <div className="document-list">
              {vtuAffiliations.map((item, index) => (
                <DocumentRow key={index} title={item.title} pdf={item.pdf} index={index} />
              ))}
            </div>
          </div>
        );
      case "nba":
        return (
          <div className="animate-fade-up">
            <div className="content-header">
              <h2 className="content-header__title">NBA Accreditations</h2>
              <p className="content-header__desc">National Board of Accreditation (NBA) status and letters across various years and departments.</p>
            </div>
            <div className="timeline-container">
              {nbaAccreditations.map((item, index) => (
                <TimelineRow key={index} title={item.title} pdf={item.pdf} index={index} />
              ))}
            </div>
          </div>
        );
      case "aicte":
        const currentAicteData = aicteArchive[activeTab] || [];
        const displayedAicte = showAllAicte ? currentAicteData : currentAicteData.slice(0, 3);
        return (
          <div className="animate-fade-up">
            <div className="content-header">
              <h2 className="content-header__title">AICTE Approvals Archive</h2>
              <p className="content-header__desc">Historical and current approvals from the All India Council for Technical Education.</p>
            </div>
            <div className="archive-tabs">
              <div className="archive-tabs__container">
                {Object.keys(aicteArchive).map((yearRange) => (
                  <button
                    key={yearRange}
                    className={`archive-tabs__btn ${activeTab === yearRange ? 'is-active' : ''}`}
                    onClick={() => {
                      setActiveTab(yearRange);
                      setShowAllAicte(false);
                    }}
                  >
                    {yearRange}
                  </button>
                ))}
              </div>
            </div>
            <div className="approvals-grid-3">
              {displayedAicte.map((item, index) => (
                <PdfCard key={index} title={item.title} pdf={item.pdf} index={index} />
              ))}
            </div>
            {currentAicteData.length > 3 && (
              <div className="load-more-container">
                <button 
                  className="load-more-btn" 
                  onClick={() => setShowAllAicte(!showAllAicte)}
                >
                  {showAllAicte ? "Show Less" : "Load More"}
                </button>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="approvals-page">
      <Header />
      
      <PageBanner
        title="Approvals & Mandatory Disclosures"
        eyebrow="QUALITY ASSURANCE"
        subtitle="NCET maintains full transparency and compliance with all governing bodies, ensuring the highest standards of technical education in India."
      />

      <main className="approvals-main">
        <div className="approvals-main__container approvals-dashboard">
          
          {/* Sidebar Navigation */}
          <aside className="dashboard-sidebar animate-fade-up delay-0">
            <h3 className="dashboard-sidebar__title">Categories</h3>
            <nav className="sidebar-nav">
              {categories.map((cat, index) => (
                <button
                  key={cat.id}
                  className={`sidebar-link ${activeCategory === cat.id ? 'is-active' : ''}`}
                  onClick={() => setActiveCategory(cat.id)}
                >
                  {cat.label}
                  <FaChevronRight size="12px" />
                </button>
              ))}
            </nav>
          </aside>

          {/* Dynamic Content Area */}
          <section className="dashboard-content animate-fade-up delay-1">
            {renderContent()}
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ApprovalsDisclosure;
